import React, { useState } from 'react';
import { mockData } from '../mock';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import TrustBadges from '../components/TrustBadges';
import ServiceAreas from '../components/ServiceAreas';
import ProcessSteps from '../components/ProcessSteps';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    howDidYouFindUs: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/quotes`, quoteForm);
      toast.success('Quote request submitted! We\'ll contact you within 24 hours.');
      setQuoteForm({
        name: '',
        email: '',
        phone: '',
        service: '',
        howDidYouFindUs: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to submit quote. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="T&M Construction"
        description="Professional construction, renovation, painting and decorating services across Suffolk, Essex, and London. 5-star rated. Free quotes. Call +44 0333 335 5532"
        canonical="/"
      />
      
      {/* Hero Section */}
      <section id="home" className="pt-56 pb-16 md:pt-32 md:pb-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-200">
                Trusted Construction Partner
              </Badge>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                Let's Build Something Amazing Together
              </h1>
              <p className="text-xl text-gray-600">
                High-quality building, renovation, and maintenance services across Suffolk, Essex, and London. With over a decade of experience, we're trusted by homeowners and businesses for our craftsmanship and reliability.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="bg-orange-600 hover:bg-orange-700 text-white px-8"
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                >
                  <LucideIcons.FileText className="w-5 h-5 mr-2" />
                  Request a Quote
                </Button>
                <Link to="/projects">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-gray-300 hover:border-orange-600 hover:text-orange-600"
                  >
                    View Our Work
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=800&fit=crop" 
                  alt="Construction site"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-black text-white p-6 rounded-xl shadow-xl">
                <div className="text-3xl font-bold text-orange-600">10+</div>
                <div className="text-sm">Years Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-black text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {mockData.stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-orange-600 mb-2">{stat.number}</div>
                <div className="text-gray-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before & After Showcase */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">See Our Transformations</h2>
              <p className="text-xl text-gray-600">Real results from our recent projects across Suffolk, Essex, and London</p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Before Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-xl group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_eb4cb118-70a1-4521-b6f1-590607903a26/artifacts/wdpazmkd_IMG_4857.jpeg" 
                  alt="Before - Front garden renovation"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-gray-900 text-white text-lg px-4 py-2">BEFORE</Badge>
                </div>
              </div>
              {/* After Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-xl group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_eb4cb118-70a1-4521-b6f1-590607903a26/artifacts/gh025qbv_IMG_5039.jpeg" 
                  alt="After - Front garden renovation complete"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-orange-600 text-white text-lg px-4 py-2">AFTER</Badge>
                </div>
              </div>
            </div>
            <div className="text-center mt-8">
              <Link to="/before-after">
                <Button 
                  size="lg" 
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  View More Transformations
                  <LucideIcons.ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Professional Construction Services</h2>
              <p className="text-xl text-gray-300">Quality workmanship in every project we undertake</p>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video bg-gray-800">
              <video 
                className="w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                poster="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&h=675&fit=crop"
              >
                <source src="https://customer-assets.emergentagent.com/job_eb4cb118-70a1-4521-b6f1-590607903a26/artifacts/1q42wbyj_266956_small.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-8">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="text-white">
                    <h3 className="text-2xl font-bold mb-1">T&M Construction</h3>
                    <p className="text-gray-200">Building dreams across Suffolk, Essex & London</p>
                  </div>
                  <Link to="/contact">
                    <Button className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 text-lg">
                      Get Free Quote
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <TrustBadges />

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">About TM Construction</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              At TM Construction, we deliver high-quality building, renovation, and maintenance services across Suffolk, Essex, and London. With over a decade of experience, we are trusted by homeowners and businesses alike for our craftsmanship, reliability, and customer focus. From complete property refurbishments to smaller home renovations, our team of expert builders ensures every project is completed to the highest standards.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 hover:border-orange-600 transition-all duration-300 hover:shadow-lg">
              <CardHeader>
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <LucideIcons.Award className="w-8 h-8 text-orange-600" />
                </div>
                <CardTitle>Quality Craftsmanship</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Every project is executed with precision and attention to detail, ensuring the highest standards of workmanship.</p>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-orange-600 transition-all duration-300 hover:shadow-lg">
              <CardHeader>
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <LucideIcons.Clock className="w-8 h-8 text-orange-600" />
                </div>
                <CardTitle>On-Time Delivery</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">We understand the importance of deadlines and consistently deliver projects on schedule without compromising quality.</p>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-orange-600 transition-all duration-300 hover:shadow-lg">
              <CardHeader>
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <LucideIcons.Users className="w-8 h-8 text-orange-600" />
                </div>
                <CardTitle>Expert Team</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Our skilled professionals bring years of experience and expertise to every project, big or small.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Explore Our Work Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Explore Our Work</h2>
            <p className="text-xl text-gray-600">Discover what we can do for your property</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Link to="/services">
              <div className="relative h-80 rounded-2xl overflow-hidden group cursor-pointer shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=800" 
                  alt="Our Services"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-3xl font-bold text-white mb-2">Our Services</h3>
                  <p className="text-gray-200 mb-4">Kitchen renovations, bathrooms, extensions, painting & more</p>
                  <div className="flex items-center text-orange-400 font-semibold group-hover:translate-x-2 transition-transform">
                    View All Services
                    <LucideIcons.ArrowRight className="w-5 h-5 ml-2" />
                  </div>
                </div>
              </div>
            </Link>
            <Link to="/projects">
              <div className="relative h-80 rounded-2xl overflow-hidden group cursor-pointer shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800" 
                  alt="Our Projects"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-3xl font-bold text-white mb-2">Our Projects</h3>
                  <p className="text-gray-200 mb-4">Browse our portfolio of completed transformations</p>
                  <div className="flex items-center text-orange-400 font-semibold group-hover:translate-x-2 transition-transform">
                    View All Projects
                    <LucideIcons.ArrowRight className="w-5 h-5 ml-2" />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact/Quote Section */}
      <section id="contact" className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Request a Free Quote</h2>
              <p className="text-xl text-gray-300 mb-8">
                Ready to start your construction or renovation project? Get in touch with us today for a free, no-obligation consultation.
              </p>
              <div className="space-y-6">
                <a href={`tel:${mockData.company.phone}`} className="flex items-start space-x-4 hover:scale-105 transition-transform">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <LucideIcons.Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-semibold text-lg">Phone</div>
                    <div className="text-gray-300 hover:text-orange-500">{mockData.company.phone}</div>
                  </div>
                </a>
                <a href={`mailto:${mockData.company.email}`} className="flex items-start space-x-4 hover:scale-105 transition-transform">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <LucideIcons.Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-semibold text-lg">Email</div>
                    <div className="text-gray-300 hover:text-orange-500">{mockData.company.email}</div>
                  </div>
                </a>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <LucideIcons.MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-semibold text-lg">Location</div>
                    <div className="text-gray-300">{mockData.company.address}</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Card className="bg-white text-gray-900">
                <CardHeader>
                  <CardTitle className="text-2xl">Send us a message</CardTitle>
                  <CardDescription>We'll get back to you within 24 hours</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleQuoteSubmit} className="space-y-4">
                    <div>
                      <Input 
                        placeholder="Your Name"
                        value={quoteForm.name}
                        onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})}
                        required
                        className="border-gray-300"
                      />
                    </div>
                    <div>
                      <Input 
                        type="email"
                        placeholder="Your Email"
                        value={quoteForm.email}
                        onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                        required
                        className="border-gray-300"
                      />
                    </div>
                    <div>
                      <Input 
                        type="tel"
                        placeholder="Your Phone"
                        value={quoteForm.phone}
                        onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})}
                        required
                        className="border-gray-300"
                      />
                    </div>
                    <div>
                      <Input 
                        placeholder="Service Needed"
                        value={quoteForm.service}
                        onChange={(e) => setQuoteForm({...quoteForm, service: e.target.value})}
                        required
                        className="border-gray-300"
                      />
                    </div>
                    <div>
                      <select
                        value={quoteForm.howDidYouFindUs}
                        onChange={(e) => setQuoteForm({...quoteForm, howDidYouFindUs: e.target.value})}
                        required
                        className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                      >
                        <option value="">How did you find us?</option>
                        <option value="Google Search">Google Search</option>
                        <option value="Facebook">Facebook</option>
                        <option value="Instagram">Instagram</option>
                        <option value="Recommendation">Recommendation from friend/family</option>
                        <option value="Drove/Walked Past">Drove/Walked Past</option>
                        <option value="Previous Customer">Previous Customer</option>
                        <option value="MyBuilder">MyBuilder</option>
                        <option value="Checkatrade">Checkatrade</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <Textarea 
                        placeholder="Tell us about your project..."
                        value={quoteForm.message}
                        onChange={(e) => setQuoteForm({...quoteForm, message: e.target.value})}
                        required
                        rows={4}
                        className="border-gray-300"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                      size="lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <LucideIcons.Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <LucideIcons.Send className="w-5 h-5 mr-2" />
                          Submit Request
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <ProcessSteps />

      {/* Service Areas */}
      <ServiceAreas />
    </div>
  );
};

export default Home;
